/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { addMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import type Appointment from 'unattended-showing/models/appointment';
import type ContextModel from 'unattended-showing/models/context';
import type LocationModel from 'unattended-showing/models/location';

interface TourDetailsSignature {
    Element: HTMLDivElement;
    Args: {
        appointment: Appointment;
        context: ContextModel;
        location: LocationModel;
        targetMoveInDate: Date;
    };
}

export default class TourDetails extends Component<TourDetailsSignature> {
    /**
     * Format the location address to display.
     */
    get formattedAddress(): string {
        const location = this.args.location;
        let addressString = location.street1 + ', ';
        if (location.street2) {
            addressString += location.street2 + ', ';
        }
        addressString +=
            location.city + ', ' + location.state + ', ' + location.zipCode;
        return addressString;
    }

    /**
     * Format the tour time range to display in the format h:mm a - h:mm a.
     */
    get formattedTourTime(): string {
        const appointmentStartTime = this.args.appointment.dateTimeUtc,
            startTimeString = this.formatTimeInTimeZone(appointmentStartTime),
            appointmentEndTime = addMinutes(
                appointmentStartTime,
                this.args.context.appointmentLength
            );
        const endTimeString = this.formatTimeInTimeZone(appointmentEndTime);
        return `${startTimeString} - ${endTimeString}`;
    }

    /**
     * Format the given date in the format h:mm a in the location's local time zone.
     */
    formatTimeInTimeZone(date: Date): string {
        const { timeZone } = this.args.location;
        return formatInTimeZone(date, timeZone, 'h:mm a');
    }

    /**
     * Get the number of bedrooms and bathrooms for the unit in a formatted string.
     */
    get formattedUnitType(): string {
        const { location } = this.args;
        return `${location.numberOfBedrooms} bd, ${location.numberOfBathrooms} ba`;
    }
}

declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        TourDetails: typeof TourDetails;
    }
}
